import type { Bookmark, BookmarksFile } from "@gitmarks/core";

// Netscape Bookmark File Format reference:
// https://msdn.microsoft.com/en-us/library/aa753582(v=vs.85).aspx
// All major browsers import this format.

interface FolderNode {
  name: string;
  bookmarks: Bookmark[];
  children: Map<string, FolderNode>;
}

function emptyFolder(name: string): FolderNode {
  return { name, bookmarks: [], children: new Map() };
}

function buildTree(bookmarks: Bookmark[]): FolderNode {
  const root = emptyFolder("");
  for (const b of bookmarks) {
    if (b.folder.length === 0) {
      root.bookmarks.push(b);
      continue;
    }
    const segments = b.folder.split("/").filter((s) => s.length > 0);
    let cursor = root;
    for (const seg of segments) {
      let next = cursor.children.get(seg);
      if (next === undefined) {
        next = emptyFolder(seg);
        cursor.children.set(seg, next);
      }
      cursor = next;
    }
    cursor.bookmarks.push(b);
  }
  return root;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function epochSeconds(iso: string): number | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 1000);
}

function renderBookmark(b: Bookmark, indent: string): string {
  const attrs: string[] = [`HREF="${escapeHtml(b.url)}"`];
  const added = epochSeconds(b.added_at);
  if (added !== null) attrs.push(`ADD_DATE="${added}"`);
  const updated = epochSeconds(b.updated_at);
  if (updated !== null) attrs.push(`LAST_MODIFIED="${updated}"`);
  if (b.tags.length > 0) attrs.push(`TAGS="${escapeHtml(b.tags.join(","))}"`);
  return `${indent}<DT><A ${attrs.join(" ")}>${escapeHtml(b.title)}</A>`;
}

function renderFolder(node: FolderNode, indent: string): string {
  const lines: string[] = [];
  if (node.name.length > 0) {
    lines.push(`${indent}<DT><H3>${escapeHtml(node.name)}</H3>`);
    lines.push(`${indent}<DL><p>`);
  }
  const inner = node.name.length > 0 ? `${indent}    ` : indent;
  for (const child of [...node.children.values()].sort((a, b) => a.name.localeCompare(b.name))) {
    lines.push(renderFolder(child, inner));
  }
  for (const b of node.bookmarks) {
    lines.push(renderBookmark(b, inner));
  }
  if (node.name.length > 0) {
    lines.push(`${indent}</DL><p>`);
  }
  return lines.join("\n");
}

export function toNetscapeHtml(file: BookmarksFile): string {
  const alive = file.bookmarks.filter((b) => b.deleted_at == null);
  const root = buildTree(alive);
  const body = renderFolder(root, "    ");
  return [
    "<!DOCTYPE NETSCAPE-Bookmark-file-1>",
    "<!-- Generated by gitmarks -->",
    '<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">',
    "<TITLE>Bookmarks</TITLE>",
    "<H1>Bookmarks</H1>",
    "<DL><p>",
    body,
    "</DL><p>",
    "",
  ].join("\n");
}
